import React, { useEffect, useMemo, useRef, useState } from 'react';
import Autosuggest from 'react-autosuggest';
import { ChevronDown, ChevronUp, Plus } from '@saleshandy/icons';
import { ImageIcon } from '@saleshandy/design-system';
import { Images } from '../../../../app-constants';
import TruncatedTextWithTooltip from '../../../../components/truncated-text-with-tooltip';

export type InlineAutosuggestItem = {
  id?: number;
  name: string;
  isNew?: boolean;
  // Arbitrary fields passthrough
  [key: string]: any;
};

type Props = {
  items: InlineAutosuggestItem[];
  placeholder?: string;
  value: string;
  onChange: (value: string) => void;
  onPickExisting: (item: InlineAutosuggestItem) => void;
  onPickNew: (name: string) => void;
  onBlur?: () => void;
  onClear?: () => void;
  className?: string;
};

const InlineAutosuggest: React.FC<Props> = ({
  items,
  placeholder = 'Type to search',
  value,
  onChange,
  onPickExisting,
  onPickNew,
  onBlur,
  onClear,
  className,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [suggestions, setSuggestions] = useState<InlineAutosuggestItem[]>([]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
    setOpen(false);
  };

  const handleOutsideClickColumnSave = () => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    const raw = (value || '').trim();
    if (!raw) return;

    // If exact match exists, select it; else treat as new
    const exact = items?.find(
      (it) => (it?.name || '').trim().toLowerCase() === raw.toLowerCase(),
    );

    if (exact) {
      if (exact?.name !== value) onChange(exact?.name);
      onPickExisting(exact);
    } else {
      onChange(raw);
      onPickNew(raw);
    }

    onSuggestionsClearRequested();
  };

  const theme = useMemo(
    () => ({
      suggestionsList: { margin: 0, padding: 0, listStyleType: 'none' } as any,
      suggestionsContainer: {
        position: 'absolute',
        zIndex: 9999,
        left: 0,
        right: 0,
      } as any,
      suggestionsContainerOpen: {
        display: 'block',
        border: '1px solid #E5E7EB',
        borderRadius: '4px',
        fontFamily: 'Inter',
        fontSize: '14px',
        lineHeight: '20px',
        marginTop: '4px',
        maxHeight: '14rem',
        overflowY: 'auto',
        backgroundColor: 'white',
        padding: '8px 0px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, .15)',
        overflowX: 'hidden',
      } as any,
      suggestionHighlighted: { backgroundColor: '#EFF6FF' } as any,
    }),
    [],
  );

  const filterItems = (text: string): InlineAutosuggestItem[] => {
    const raw = (text || '').trim();
    if (!raw) return items;

    const lowerRaw = raw?.toLowerCase();

    const filtered = items?.filter((it) =>
      (it?.name || '')?.toLowerCase()?.includes(lowerRaw),
    );

    // Include Add row only if there is no exact name match (case-insensitive)
    const hasExactMatch = items?.some(
      (it) => (it?.name || '')?.trim()?.toLowerCase() === lowerRaw,
    );

    if (!hasExactMatch) {
      filtered?.push({ name: raw, isNew: true });
    }

    return filtered;
  };

  const onSuggestionsFetchRequested = ({ value: v }: any) => {
    setSuggestions(filterItems(v));
    setOpen(true);
  };

  const handleClear = () => {
    // Clear the input value first
    onChange?.('');
    onClear?.();

    // Use the exact same flow as natural input changes
    // This will trigger onSuggestionsFetchRequested with empty value
    // which will call filterItems('') and return all items
    setTimeout(() => {
      onSuggestionsFetchRequested({ value: '' });
    }, 0);

    // Focus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const getSuggestionValue = (s: InlineAutosuggestItem) => s.name;

  const onSuggestionSelected = (
    e: any,
    { suggestion }: { suggestion: InlineAutosuggestItem },
  ) => {
    // Blur the input to remove focus and prevent unwanted outside click triggers
    if (inputRef.current) {
      inputRef.current.blur();
    }

    if (suggestion?.isNew) {
      e?.preventDefault?.();
      onChange((suggestion.name || '').trim());
      onPickNew((suggestion.name || '').trim());
      onSuggestionsClearRequested();
    } else {
      onChange(suggestion.name || '');
      onPickExisting(suggestion);
      onSuggestionsClearRequested();
    }
  };

  const renderSuggestion = (suggestion: InlineAutosuggestItem) => {
    if (suggestion?.isNew) {
      return (
        <div className="inline-dropdown-add-new-column-container">
          <Plus color="#1D4ED8" />{' '}
          <span className="inline-dropdown-add-column">
            Create new column "{value}"
          </span>
        </div>
      );
    }

    return (
      <div className="bs-py-6 bs-px-12 d-flex align-items-center justify-content-between">
        {/* {suggestion.name} */}
        <TruncatedTextWithTooltip string={suggestion?.name} length={35} />
        {suggestion?.isAiGenerated && (
          <ImageIcon
            src={Images?.NoResultGradient}
            alt="no-result-gradient"
            width={16}
            height={16}
          />
        )}
      </div>
    );
  };

  const renderInputComponent = (props: any) => {
    const isInputAiGenerated = items.some(
      (it) =>
        (it.name || '').trim().toLowerCase() ===
          (value || '').trim().toLowerCase() &&
        Boolean((it as any).isAiGenerated),
    );

    return (
      <div className={`inline-autosuggest-input-wrapper ${className || ''}`}>
        <input {...props} className="bs-input has-dropdown-icon" />
        {isInputAiGenerated && (
          <span className="inline-autosuggest-ai-indicator">
            <ImageIcon
              src={Images?.NoResultGradient}
              alt="ai-generated"
              width={16}
              height={16}
            />
          </span>
        )}
        {value && value.length > 0 && onClear && (
          <button
            type="button"
            className="inline-autosuggest-clear"
            onClick={handleClear}
            aria-label="Clear input"
          >
            <i className="sh-close-o cursor-pointer" />
          </button>
        )}
        <button
          type="button"
          className="inline-autosuggest-toggle"
          onClick={() => {
            if (open) {
              onSuggestionsClearRequested();
            } else {
              setSuggestions(filterItems(value));
              setOpen(true);
              inputRef.current?.focus();
            }
          }}
          aria-label={open ? 'Collapse options' : 'Expand options'}
        >
          {open ? <ChevronUp /> : <ChevronDown />}
        </button>
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        // Also check if input is focused - if not, don't process the click
        if (inputRef.current && document.activeElement !== inputRef.current) {
          return;
        }
        handleOutsideClickColumnSave();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [
    value,
    items,
    onChange,
    onPickExisting,
    onPickNew,
    handleOutsideClickColumnSave,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const raw = (value || '').trim();
      if (!raw) return;

      // Check if there's an exact match
      const exact = items?.find(
        (it) => (it?.name || '').trim().toLowerCase() === raw.toLowerCase(),
      );

      if (exact) {
        // If exact match exists, select it
        onChange(exact.name);
        onPickExisting(exact);
      } else {
        // If no exact match, treat as new column
        onChange(raw);
        onPickNew(raw);
      }

      onSuggestionsClearRequested();
    }
  };

  const inputProps = {
    placeholder,
    value,
    onChange: (_e: any, { newValue }: any) => onChange(newValue),
    onFocus: () => {
      setSuggestions(filterItems(value));
      setOpen(true);
    },
    onClick: () => {
      setSuggestions(filterItems(value));
      setOpen(true);
    },
    onKeyDown: handleKeyDown,
    onBlur,

    ref: (ref: HTMLInputElement) => {
      inputRef.current = ref;
    },
  } as any;

  return (
    <div className="inline-autosuggest-container" ref={containerRef}>
      <Autosuggest
        suggestions={[...suggestions]}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        getSuggestionValue={getSuggestionValue}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
        renderInputComponent={renderInputComponent}
        theme={theme}
        onSuggestionSelected={onSuggestionSelected}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        shouldRenderSuggestions={() => true}
        className="inline-autosuggest-dropdown-column"
        focusInputOnSuggestionClick={false}
      />
    </div>
  );
};

export default InlineAutosuggest;
